import React, { useState } from 'react';
import { FaRupeeSign, FaChartLine, FaCalendarAlt, FaInfo, FaShieldAlt, FaQuestion, FaChartPie, FaCalculator, FaBuilding, FaWallet, FaPiggyBank, FaMoneyBillWave, FaBank, FaClock, FaMoneyBill, FaChartBar, FaChartArea, FaChartColumn } from 'react-icons/fa';
import { Helmet } from 'react-helmet';
import Chart from 'react-apexcharts';

const SWPCalculator = () => {
  const [formData, setFormData] = useState({
    initialInvestment: '',
    monthlyWithdrawal: '',
    years: '',
    expectedReturn: '8',
  });

  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const calculateSWP = () => {
    const { initialInvestment, monthlyWithdrawal, years, expectedReturn } = formData;

    if (!initialInvestment || !monthlyWithdrawal || !years) {
      setError('Please fill all fields');
      return;
    }

    if (initialInvestment < 10000) {
      setError('Minimum initial investment is ₹10,000');
      return;
    }

    if (monthlyWithdrawal < 1000) {
      setError('Minimum monthly withdrawal is ₹1,000');
      return;
    }

    if (years < 1 || years > 30) {
      setError('Investment period must be between 1 and 30 years');
      return;
    }

    const rate = parseFloat(expectedReturn) / 100;
    const months = years * 12;
    const monthlyRate = rate / 12;
    const initialAmount = parseFloat(initialInvestment);
    const monthlyWithdraw = parseFloat(monthlyWithdrawal);

    let remainingAmount = initialAmount;
    const monthlyBalances = [initialAmount];
    const monthlyWithdrawals = [];

    for (let i = 1; i <= months; i++) {
      // Calculate interest for the month
      const interest = remainingAmount * monthlyRate;
      remainingAmount += interest;
      
      // Withdraw amount
      remainingAmount -= monthlyWithdraw;
      
      // Store values
      monthlyBalances.push(remainingAmount);
      monthlyWithdrawals.push(monthlyWithdraw);
    }

    setResult({
      monthlyBalances,
      monthlyWithdrawals,
      months,
      initialAmount,
      monthlyWithdraw,
      expectedReturn: parseFloat(expectedReturn),
    });
    setError('');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  const getChartData = () => {
    if (!result) return {};

    const { monthlyBalances, months } = result;
    const data = [];

    for (let i = 0; i <= months; i++) {
      data.push({
        x: i,
        y: monthlyBalances[i]
      });
    }

    return {
      options: {
        chart: {
          type: 'line',
          height: 350,
          toolbar: {
            show: false
          },
          animations: {
            enabled: false
          }
        },
        colors: ['#6366F1', '#10B981'],
        xaxis: {
          type: 'numeric',
          title: {
            text: 'Months',
            style: {
              color: '#1F2937',
              fontSize: '14px'
            }
          },
          labels: {
            style: {
              colors: '#6B7280',
              fontSize: '12px'
            },
            formatter: function (value) {
              return value;
            }
          },
          tickAmount: 10
        },
        yaxis: {
          title: {
            text: 'Amount (₹)',
            style: {
              color: '#1F2937',
              fontSize: '14px'
            }
          },
          labels: {
            style: {
              colors: '#6B7280',
              fontSize: '12px'
            },
            formatter: function (value) {
              return new Intl.NumberFormat('en-IN').format(value);
            }
          },
          min: 0
        },
        title: {
          text: 'Portfolio Value Over Time',
          align: 'left',
          style: {
            fontSize: '16px',
            fontWeight: 'bold',
            color: '#1F2937'
          }
        },
        tooltip: {
          enabled: true,
          y: {
            formatter: function (value) {
              return formatCurrency(value);
            }
          }
        },
        grid: {
          show: true,
          borderColor: '#e5e7eb',
          strokeDashArray: 2,
          position: 'back'
        },
        stroke: {
          curve: 'smooth',
          width: 2
        }
      },
      series: [{
        name: 'Portfolio Value',
        data: data
      }]
    };
  };

  return (
    <div className="bg-gradient-to-br from-purple-50 to-white min-h-screen p-8">
      <Helmet>
        <title>SWP Calculator - Systematic Withdrawal Plan</title>
        <meta name="description" content="Calculate your SWP returns and portfolio value over time." />
        <meta property="og:title" content="SWP Calculator" />
        <meta property="og:description" content="Calculate your SWP returns and portfolio value over time." />
        <meta property="og:type" content="website" />
      </Helmet>

      <div className="bg-white rounded-3xl shadow-2xl p-10 max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-10">
          <h2 className="text-4xl font-bold text-gray-800 flex items-center">
            <FaMoneyBill className="mr-3 text-4xl text-purple-600" />
            SWP Calculator
          </h2>
          <div className="flex items-center space-x-4">
            <span className="text-sm text-gray-600">Expected Return Rate:</span>
            <span className="px-4 py-1 bg-purple-100 text-purple-800 rounded-full text-sm font-medium">
              {formData.expectedReturn}%
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-10">
          <div className="bg-gradient-to-r from-purple-50 to-white p-6 rounded-2xl shadow-md hover:shadow-lg transition-shadow duration-300">
            <label className="block text-purple-700 text-sm font-bold mb-3 flex items-center">
              <FaWallet className="inline-block mr-2 text-purple-500" />
              Initial Investment Amount (₹)
            </label>
            <div className="relative">
              <FaRupeeSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-purple-400" />
              <input
                type="number"
                name="initialInvestment"
                value={formData.initialInvestment}
                onChange={handleInputChange}
                className="w-full pl-12 pr-4 py-3 border border-purple-200 rounded-lg focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-all duration-300"
                placeholder="Enter initial investment"
                min="10000"
              />
            </div>
          </div>

          <div className="bg-gradient-to-r from-purple-50 to-white p-6 rounded-2xl shadow-md hover:shadow-lg transition-shadow duration-300">
            <label className="block text-purple-700 text-sm font-bold mb-3 flex items-center">
              <FaChartLine className="inline-block mr-2 text-purple-500" />
              Monthly Withdrawal Amount (₹)
            </label>
            <div className="relative">
              <FaRupeeSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-purple-400" />
              <input
                type="number"
                name="monthlyWithdrawal"
                value={formData.monthlyWithdrawal}
                onChange={handleInputChange}
                className="w-full pl-12 pr-4 py-3 border border-purple-200 rounded-lg focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-all duration-300"
                placeholder="Enter monthly withdrawal"
                min="1000"
              />
            </div>
          </div>

          <div className="bg-gradient-to-r from-purple-50 to-white p-6 rounded-2xl shadow-md hover:shadow-lg transition-shadow duration-300">
            <label className="block text-purple-700 text-sm font-bold mb-3 flex items-center">
              <FaCalendarAlt className="inline-block mr-2 text-purple-500" />
              Investment Period (Years)
            </label>
            <select
              value={formData.years}
              onChange={handleInputChange}
              name="years"
              className="w-full p-3 border border-purple-200 rounded-lg focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-all duration-300"
            >
              {Array.from({ length: 30 }, (_, i) => i + 1).map(year => (
                <option key={year} value={year}>{year} Year{year !== 1 ? 's' : ''}</option>
              ))}
            </select>
          </div>

          <div className="bg-gradient-to-r from-purple-50 to-white p-6 rounded-2xl shadow-md hover:shadow-lg transition-shadow duration-300">
            <label className="block text-purple-700 text-sm font-bold mb-3 flex items-center">
              <FaChartBar className="inline-block mr-2 text-purple-500" />
              Expected Return Rate (%)
            </label>
            <select
              value={formData.expectedReturn}
              onChange={handleInputChange}
              name="expectedReturn"
              className="w-full p-3 border border-purple-200 rounded-lg focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-all duration-300"
            >
              <option value="6">6%</option>
              <option value="7">7%</option>
              <option value="8">8%</option>
              <option value="9">9%</option>
              <option value="10">10%</option>
              <option value="11">11%</option>
              <option value="12">12%</option>
            </select>
          </div>
        </div>

        <button
          onClick={calculateSWP}
          className="w-full bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-black font-bold py-6 px-12 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 mb-10 border-4 border-purple-500 flex items-center justify-center gap-3"
        >
          <FaCalculator className="inline-block text-2xl" />
          <span>Calculate SWP Returns</span>
        </button>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-6 py-4 rounded-lg mb-6">
            {error}
          </div>
        )}

        {result && (
          <div className="bg-white p-8 rounded-2xl shadow-lg mb-8">
            <h2 className="text-2xl font-semibold mb-6 flex items-center">
              <FaChartArea className="mr-3 text-purple-600" />
              Portfolio Analysis
            </h2>
            <div className="bg-white p-6 rounded-xl shadow-md">
              <Chart options={getChartData().options} series={getChartData().series} type="line" height={350} />
            </div>
            <div className="mt-6 p-6 bg-purple-50 rounded-xl">
              <h3 className="text-lg font-semibold mb-3 text-purple-700">Summary</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Initial Investment</span>
                  <span className="font-semibold text-purple-600">
                    {formatCurrency(result.initialAmount)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Monthly Withdrawal</span>
                  <span className="font-semibold text-purple-600">
                    {formatCurrency(result.monthlyWithdraw)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Expected Return Rate</span>
                  <span className="font-semibold text-purple-600">
                    {result.expectedReturn}%
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Total Months</span>
                  <span className="font-semibold text-purple-600">
                    {result.months}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="bg-white p-8 rounded-2xl shadow-lg mb-8">
          <h2 className="text-2xl font-semibold mb-6 flex items-center">
            <FaInfo className="mr-3 text-purple-600" />
            About SWP
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="p-6 bg-purple-50 rounded-xl">
              <h3 className="text-xl font-semibold mb-4 text-purple-700">Key Features</h3>
              <ul className="list-disc list-inside text-gray-600 space-y-2">
                <li>Regular income from investments</li>
                <li>Flexibility in withdrawal amount</li>
                <li>Tax-efficient income generation</li>
                <li>Ideal for retirement planning</li>
                <li>No lock-in period</li>
              </ul>
            </div>
            <div className="p-6 bg-purple-50 rounded-xl">
              <h3 className="text-xl font-semibold mb-4 text-purple-700">Benefits</h3>
              <ul className="list-disc list-inside text-gray-600 space-y-2">
                <li>Steady income stream</li>
                <li>Capital appreciation potential</li>
                <li>Tax benefits under Section 112A</li>
                <li>Flexibility to adjust withdrawals</li>
                <li>No exit load after 1 year</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="bg-white p-8 rounded-2xl shadow-lg mb-8">
          <h2 className="text-2xl font-semibold mb-6 flex items-center">
            <FaQuestion className="mr-3 text-purple-600" />
            Frequently Asked Questions
          </h2>
          <div className="space-y-6">
            <div className="bg-purple-50 p-6 rounded-xl">
              <h3 className="text-lg font-semibold mb-3 text-purple-700">What is SWP?</h3>
              <p className="text-gray-600">
                SWP (Systematic Withdrawal Plan) is a facility that allows investors to withdraw a fixed amount from their mutual fund investments at regular intervals.
              </p>
            </div>
            <div className="bg-purple-50 p-6 rounded-xl">
              <h3 className="text-lg font-semibold mb-3 text-purple-700">What is the minimum investment?</h3>
              <p className="text-gray-600">
                The minimum initial investment amount is ₹10,000.
              </p>
            </div>
            <div className="bg-purple-50 p-6 rounded-xl">
              <h3 className="text-lg font-semibold mb-3 text-purple-700">What is the minimum withdrawal amount?</h3>
              <p className="text-gray-600">
                The minimum monthly withdrawal amount is ₹1,000.
              </p>
            </div>
            <div className="bg-purple-50 p-6 rounded-xl">
              <h3 className="text-lg font-semibold mb-3 text-purple-700">Is there any tax on SWP?</h3>
              <p className="text-gray-600">
                Long-term capital gains from SWP are taxed at 10% without indexation if gains exceed ₹1 lakh in a financial year.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-8 rounded-2xl shadow-lg">
          <h2 className="text-2xl font-semibold mb-6 flex items-center">
            <FaInfo className="mr-3 text-purple-600" />
            How to Use
          </h2>
          <div className="space-y-6">
            <div className="bg-purple-50 p-6 rounded-xl">
              <h3 className="text-lg font-semibold mb-3 text-purple-700">Step 1: Enter Initial Investment</h3>
              <p className="text-gray-600">
                Input the initial amount you wish to invest in mutual funds (minimum ₹10,000).
              </p>
            </div>
            <div className="bg-purple-50 p-6 rounded-xl">
              <h3 className="text-lg font-semibold mb-3 text-purple-700">Step 2: Set Monthly Withdrawal</h3>
              <p className="text-gray-600">
                Choose your preferred monthly withdrawal amount (minimum ₹1,000).
              </p>
            </div>
            <div className="bg-purple-50 p-6 rounded-xl">
              <h3 className="text-lg font-semibold mb-3 text-purple-700">Step 3: Select Investment Period</h3>
              <p className="text-gray-600">
                Choose your preferred investment period (1-30 years).
              </p>
            </div>
            <div className="bg-purple-50 p-6 rounded-xl">
              <h3 className="text-lg font-semibold mb-3 text-purple-700">Step 4: Calculate Returns</h3>
              <p className="text-gray-600">
                Click the "Calculate SWP Returns" button to see your portfolio value and withdrawal schedule.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SWPCalculator;
